import { IconName, IconPrefix } from '@fortawesome/fontawesome-common-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect } from 'react'

export type MatchableTask = {
  task_id: number
  type: string
  service_name: string | null
  task_name: string | null
  file_path: string | null
  url: string | null
  icon: string | null
  icon_color: string | null
}

type Props = {
  previewTasks: MatchableTask[]
  services: string[]
  keywordWords: string[]
  keywordOperator: string
  filePaths: string[]
  urlWords: string[]
  urlPattern: string
  onMatchedIdsChange: (ids: number[]) => void
}

const includesAny = (target: string | null, words: string[]): boolean => {
  if (!target) return false
  return words.some((word) => target.includes(word))
}

const matchTasksByCondition = (
  tasks: MatchableTask[],
  condition: {
    services: string[]
    keyword: { operator: string; words: string[] }
    filePaths: string[]
    url: { pattern: string; words: string[] }
  },
): MatchableTask[] => {
  const checks: ((task: MatchableTask) => boolean)[] = []
  if (condition.services.length) {
    checks.push((task) => includesAny(task.service_name, condition.services))
  }
  if (condition.keyword.words.length) {
    checks.push((task) => {
      if (!task.task_name) return false
      if (condition.keyword.operator === 'and') {
        return condition.keyword.words.every((w) => task.task_name?.includes(w))
      }
      return condition.keyword.words.some((w) => task.task_name?.includes(w))
    })
  }
  if (condition.url.words.length) {
    checks.push((task) => {
      if (!task.url) return false
      if (condition.url.pattern === 'exact') return condition.url.words.some((w) => task.url === w)
      return includesAny(task.url, condition.url.words)
    })
  }
  if (condition.filePaths.length) {
    checks.push((task) => includesAny(task.file_path, condition.filePaths))
  }
  if (!checks.length) return []
  return tasks.filter((task) => checks.every((check) => check(task)))
}

const AutoLabelingTargetPreview: FC<Props> = ({
  previewTasks,
  services,
  keywordWords,
  keywordOperator,
  filePaths,
  urlWords,
  urlPattern,
  onMatchedIdsChange,
}) => {
  const matchedTasks = matchTasksByCondition(previewTasks, {
    services,
    keyword: { operator: keywordOperator, words: keywordWords },
    filePaths,
    url: { pattern: urlPattern, words: urlWords },
  })
  const matchedTaskIds = new Set(matchedTasks.map((task) => task.task_id))

  useEffect(() => {
    onMatchedIdsChange(Array.from(matchedTaskIds))
  })

  return (
    <>
      {
        previewTasks.length ? (
          <div className="matched_task_list">
            {
              previewTasks.map((task) => (
                <div
                  className={`matched_task_row ${matchedTaskIds.has(task.task_id) ? "matched" : ""}`}
                  key={`matched_task_${task.task_id}`}
                >
                  <span
                    className="matched_task_icon"
                    style={{ color: task.icon_color || "#666666" }}
                    title={task.service_name || ""}
                  >
                    {
                      task.icon ? (
                        <FontAwesomeIcon
                          icon={[task.icon.split(" ")[0] as IconPrefix, task.icon.split(" ")[1] as IconName]}
                          fixedWidth
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={task.type === "OnlineMeetingTask" ? ["fas", "video"] : ["fas", "check-circle"]}
                          fixedWidth
                        />
                      )
                    }
                  </span>
                  <div className="matched_task_text">
                    <span className="matched_task_name">
                      {task.task_name || "(名称なし)"}
                    </span>
                    {
                      (task.file_path || task.url) && (
                        <span
                          className="matched_task_path"
                          title={task.file_path || task.url || ""}
                        >
                          {`(${task.file_path || task.url})`}
                        </span>
                      )
                    }
                  </div>
                </div>
              ))
            }
          </div>
        ) : (
          <div className="note">
            当日付の未仕分けタスクはありません
          </div>
        )
      }
    </>
  )
}

export default AutoLabelingTargetPreview
